import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../../services/api';

/**
 * Navbar - Navegación Principal
 * 
 * Navbar responsive con logo, menú y botones de acción.
 * Detecta estado de autenticación básico vía localStorage.
 * Updated: 2026-01-25
 */
const Navbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = React.useState(false);

    // Check auth state
    const isLoggedIn = !!localStorage.getItem('token');
    const userRole = localStorage.getItem('userRole');
    const userName = localStorage.getItem('userName') || 'Usuario';

    const [scrolled, setScrolled] = React.useState(false);

    // Rutas donde la navbar empieza transparente
    const transparentRoutes = ['/', '/pricing', '/about'];
    const isTransparentPage = transparentRoutes.includes(location.pathname);

    React.useEffect(() => {
        const handleScroll = () => {
            const isScrolled = window.scrollY > 20;
            if (isScrolled !== scrolled) {
                setScrolled(isScrolled);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [scrolled]);

    const isActive = (path) => {
        return location.pathname === path;
    };

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
    };

    const handleLogout = () => {
        logout();
        setIsMenuOpen(false);
        localStorage.removeItem('admin_forced'); // Limpiar flag de parche
        navigate('/login');
    };

    const isOrganizer = userRole === 'organizer' || userRole === 'super_admin';

    return (
        <nav className={`fixed top-0 w-full z-50 transition-all duration-300 border-b border-transparent ${
            scrolled || !isTransparentPage 
                ? 'bg-white/95 backdrop-blur-md shadow-lg py-2' 
                : 'bg-transparent py-6'
        }`}>
            <div className="container mx-auto px-4">
                <div className="flex items-center justify-between h-24">
                    {/* Logo */}
                    <Link to="/" className="flex items-center gap-2 group">
                        <img 
                            src="/visiopadel-logo.png" 
                            alt="VisioPadel" 
                            className="h-16 w-auto object-contain" 
                        />
                    </Link>

                    {/* Desktop Menu */}
                    <div className="hidden md:flex items-center gap-8">
                        {/* Gestiona tu club (Disabled for now) */}
                        <div className="flex flex-col items-center opacity-60 cursor-not-allowed">
                            <div className="flex items-center gap-1.5 font-bold text-[15px] text-slate-900">
                                Gestiona tu club
                                <svg className="w-3.5 h-3.5 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </div>
                            <span className="text-[8px] bg-slate-900 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-widest border border-slate-900 shadow-sm whitespace-nowrap -mt-1">PRÓXIMAMENTE</span>
                        </div>

                        <Link
                            to="/pricing"
                            className={`nav-link-xporty font-bold text-[15px] transition-all whitespace-nowrap ${isActive('/pricing')
                                ? 'text-[#72c02c] active'
                                : 'text-slate-900 hover:text-[#72c02c]'
                                }`}
                        >
                            Organiza tu evento
                        </Link>

                        <Link
                            to="/about"
                            className={`nav-link-xporty font-bold text-[15px] transition-all whitespace-nowrap ${isActive('/about')
                                ? 'text-[#72c02c] active'
                                : 'text-slate-900 hover:text-[#72c02c]'
                                }`}
                        >
                            Cómo Funciona
                        </Link>
                    </div>

                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-3">
                        {isLoggedIn ? (
                            <>
                                {isOrganizer && (
                                    <>
                                        {userRole === 'super_admin' && (
                                            <Link
                                                to="/master-admin"
                                                className="bg-slate-900 hover:bg-slate-800 text-primary-400 font-bold py-2.5 px-5 rounded-xl transition-all shadow-lg flex items-center gap-2 text-sm border border-slate-700"
                                            >
                                                🛡️ Master
                                            </Link>
                                        )}
                                        <Link
                                            to="/dashboard"
                                            className="bg-white/10 hover:bg-white/20 text-slate-900 font-semibold py-2.5 px-5 rounded-xl transition-all shadow-sm flex items-center gap-2 text-sm backdrop-blur-sm border border-slate-200"
                                        >
                                            📊 Dashboard
                                        </Link>

                                        <Link
                                            to="/events/create"
                                            className="bg-primary-500 hover:bg-primary-600 text-white font-semibold py-2.5 px-5 rounded-xl transition-all shadow-sm flex items-center gap-2 text-sm"
                                        >
                                            <span>+</span> Crear Pozo
                                        </Link>
                                    </>
                                )}

                                <button
                                    onClick={handleLogout}
                                    className="text-gray-400 hover:text-red-400 font-bold text-xs uppercase tracking-widest px-3 transition-colors"
                                >
                                    Salir
                                </button>

                                <div className="h-10 w-10 rounded-xl bg-primary-100 flex items-center justify-center text-primary-700 font-black border border-primary-200 shadow-sm" title={userName}>
                                    {userName.charAt(0).toUpperCase()}
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center gap-6">
                                <Link
                                    to="/login"
                                    className={`nav-link-xporty font-bold text-[15px] transition-all whitespace-nowrap ${isActive('/login')
                                        ? 'text-[#72c02c] active'
                                        : 'text-slate-900 hover:text-[#72c02c]'
                                        }`}
                                >
                                    Entrar
                                </Link>

                                <Link
                                    to="/signup"
                                    className="bg-[#72c02c] hover:bg-[#65a30d] text-white font-black py-3 px-8 rounded-full text-[14px] shadow-lg shadow-[#72c02c]/20 transition-all transform hover:-translate-y-0.5 active:scale-95"
                                >
                                    Registrarse
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button
                        onClick={toggleMenu}
                        className={`md:hidden p-2 transition-colors rounded-lg ${
                            scrolled || !isTransparentPage 
                                ? 'text-slate-900 hover:bg-slate-100' 
                                : 'text-white hover:bg-white/10'
                        }`}
                        aria-label={isMenuOpen ? "Cerrar menú" : "Abrir menú"}
                    >
                        {isMenuOpen ? (
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        ) : (
                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Mobile Menu */}
                {isMenuOpen && (
                    <div className="md:hidden bg-[#1a1a1a] border-t border-gray-800 py-4 animate-slide-in-up">
                        <div className="flex flex-col gap-3 px-2">
                            <div className="py-2 px-4 rounded-lg text-gray-400 cursor-not-allowed opacity-60 flex flex-col gap-0.5">
                                <span className="font-medium">Gestiona tu club</span>
                                <span className="text-[8px] bg-slate-900 text-white px-2 py-0.5 rounded-full font-bold uppercase tracking-widest border border-slate-900">PRÓXIMAMENTE</span>
                            </div>

                            <Link
                                to="/pricing"
                                className={`font-medium py-2 px-4 rounded-lg transition-colors ${isActive('/pricing')
                                    ? 'bg-[#00e5ff]/10 text-[#00e5ff]'
                                    : 'text-gray-300 hover:bg-white/5'
                                    }`}
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Organiza tu evento
                            </Link>

                            <Link
                                to="/about"
                                className={`font-medium py-2 px-4 rounded-lg transition-colors ${isActive('/about')
                                    ? 'bg-[#00e5ff]/10 text-[#00e5ff]'
                                    : 'text-gray-300 hover:bg-white/5'
                                    }`}
                                onClick={() => setIsMenuOpen(false)}
                            >
                                Cómo Funciona
                            </Link>

                            <div className="border-t border-gray-200 my-2" />

                            {isLoggedIn ? (
                                <>
                                    {isOrganizer && (
                                        <Link
                                            to="/events/create"
                                            className="bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-4 rounded-xl text-center mx-2 shadow-lg mb-2"
                                            onClick={() => setIsMenuOpen(false)}
                                        >
                                            + Crear Nuevo Pozo
                                        </Link>
                                    )}

                                    <button
                                        onClick={handleLogout}
                                        className="text-gray-500 hover:bg-red-50 font-bold py-3 px-4 rounded-xl text-left uppercase tracking-widest text-xs"
                                    >
                                        Cerrar Sesión
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className="text-gray-300 hover:bg-white/5 font-medium py-2 px-4 rounded-lg"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Entrar
                                    </Link>

                                    <Link
                                        to="/signup"
                                        className="bg-[#00e5ff] text-slate-900 font-bold py-3 px-4 rounded-xl text-center mx-2 shadow-lg mt-2"
                                        onClick={() => setIsMenuOpen(false)}
                                    >
                                        Registrarse
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </nav >
    );
};

export default Navbar;
